import React from 'react';
import { colors, fonts, radius, spacing } from './theme';
import { db } from '../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { isChunkError, attemptChunkRecovery } from '../lib/chunkRecovery';
import { getCurrentUser } from '../services/auth';

// RW-1535: resolve the reporting uid centrally, because every call site had
// independently lost it — src/index.js hardcoded 'anonymous', deadLink /
// callableErrors / socialWriteFailure passed nothing, and ErrorBoundary read a
// `user` prop that none of its 12 mount sites supplies. Net effect in prod: 291
// of 293 errorLogs (and 107 of 107 crashes) carried 'anonymous', so no report
// could be tied to a session and RW-1535 was untraceable by construction.
// Resolving here means a new call site cannot reintroduce the gap.
//
// uid ONLY — never email or display name (Rule 22: log record IDs, not contents).
// Auth may not be initialized yet when a very early error fires, so this never
// throws: an unattributed log is still worth keeping.
export function resolveUserId(supplied) {
  if (supplied && supplied !== 'anonymous') return supplied;
  try {
    return getCurrentUser()?.uid || 'anonymous';
  } catch {
    return 'anonymous';
  }
}

function shouldLogError(message) {
  try {
    const key = 'reve_err_dedupe';
    const raw = localStorage.getItem(key);
    const map = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    if (map[message] && now - map[message] < 5 * 60 * 1000) return false;
    map[message] = now;
    const keys = Object.keys(map);
    if (keys.length > 50) delete map[keys[0]];
    localStorage.setItem(key, JSON.stringify(map));
    return true;
  } catch { return true; }
}

export async function logErrorToFirestore(payload) {
  // RW-243 Phase 3a: write-time filter for 'Loading chunk' / 'ChunkLoadError' removed — auto-Linear-sync's filter still suppresses ticket noise. Restored observability for incidence-by-route analysis (route field added in src/index.js chunk-error payload).
  if (!shouldLogError(payload.message || 'unknown')) return;
  try {
    await addDoc(collection(db, 'errorLogs'), {
      ...payload,
      // After the spread: the resolved uid wins over a caller's stale default.
      userId: resolveUserId(payload.userId),
      stack: (payload.stack || '').slice(0, 2000),
      componentStack: (payload.componentStack || '').slice(0, 2000),
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: serverTimestamp(),
      resolved: false,
    });
  } catch (e) {
    console.warn('error log failed', e);
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // A stale-chunk / version-skew error that reaches the boundary (e.g. a
    // React.lazy child whose chunk failed to load, or whose exports mismatch the
    // running bundle after a deploy) is recoverable by reloading to a consistent
    // bundle set — don't log it as a crash (RW-859). attemptChunkRecovery's
    // counter bails out and logs if the reload doesn't actually fix it.
    if (isChunkError(error?.message, error?.stack)) {
      attemptChunkRecovery({
        message: error?.message,
        stack: error?.stack,
        log: logErrorToFirestore,
      });
      return;
    }
    logErrorToFirestore({
      type: 'frontend_crash',
      message: error?.message || String(error),
      stack: error?.stack || '',
      componentStack: info?.componentStack || '',
      // Optional explicit override; resolveUserId falls back to the signed-in
      // uid when a mount site doesn't pass `user` (none currently do).
      userId: this.props.user?.uid,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div data-html2canvas-ignore="true" style={{
          padding: spacing.xl, textAlign: 'center', maxWidth: 500, margin: '40px auto',
          background: colors.white, borderRadius: radius.lg, border: `1px solid ${colors.pearl}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: colors.noir, fontFamily: fonts.heading }}>
            {this.props.fallbackTitle || 'Something went wrong'}
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: colors.gray2, fontFamily: fonts.body }}>
            This section encountered an error. Try refreshing the page.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => { this.setState({ hasError: false, error: null }); }}
              style={{
                padding: '10px 24px', borderRadius: radius.sm, border: 'none',
                background: colors.coral, color: colors.white, fontSize: 14,
                fontWeight: 600, fontFamily: fonts.body, cursor: 'pointer',
              }}
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 24px', borderRadius: radius.sm,
                border: `1px solid ${colors.coral}`, background: 'transparent',
                color: colors.coralInk, fontSize: 14, fontWeight: 600,
                fontFamily: fonts.body, cursor: 'pointer',
              }}
            >
              Reload Page
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/'; }}
              style={{
                padding: '10px 24px', borderRadius: radius.sm,
                border: `1px solid ${colors.gray3 || '#ccc'}`, background: 'transparent',
                color: colors.gray2 || '#666', fontSize: 14, fontWeight: 600,
                fontFamily: fonts.body, cursor: 'pointer',
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
